/**
 * Tirage Relation Mappers
 *
 * Maps TirageIn* relations to ArtworkIn* format for compatibility with compute field functions.
 * This is necessary because TirageInExhibition, TirageInAuction, and TirageInBibliography
 * use different field names than their Artwork equivalents, but the compute field functions
 * expect the Artwork format.
 *
 * @module lib/tirage-relation-mappers
 */

import {
    ArtworkInAuction,
    ArtworkInBibliography,
    ArtworkInExhibition,
    TirageInAuction,
    TirageInBibliography,
    TirageInExhibition,
} from "./types";

/**
 * Maps a TirageInExhibition to ArtworkInExhibition format
 */
export function mapTirageInExhibitionToArtwork(
  tirageExhibition: TirageInExhibition
): Pick<ArtworkInExhibition, "exhibition" | "oeuvresDansExpositionsDescriptionNumeroCatalogue" | "oeuvresDansExpositionsNotes"> {
  return {
    exhibition: tirageExhibition.exhibition,
    oeuvresDansExpositionsDescriptionNumeroCatalogue:
      tirageExhibition.tirageDansExpositionsDescriptionNumeroCatalogue,
    oeuvresDansExpositionsNotes: tirageExhibition.tirageDansExpositionsNotes || "",
  };
}

/**
 * Maps an array of TirageInExhibition to ArtworkInExhibition format
 */
export function mapTirageInExhibitionsToArtwork(
  tirageExhibitions: TirageInExhibition[] | undefined | null
): Pick<ArtworkInExhibition, "exhibition" | "oeuvresDansExpositionsDescriptionNumeroCatalogue" | "oeuvresDansExpositionsNotes">[] | undefined {
  if (!tirageExhibitions || tirageExhibitions.length === 0) {
    return undefined;
  }
  return tirageExhibitions.map(mapTirageInExhibitionToArtwork);
}

/**
 * Maps a TirageInAuction to ArtworkInAuction format
 */
export function mapTirageInAuctionToArtwork(
  tirageAuction: TirageInAuction
): Pick<
  ArtworkInAuction,
  | "auction"
  | "oeuvresDansVentesDescriptionNumeroLot"
  | "oeuvresDansVentesDescriptionReproduitPage"
  | "oeuvresDansVentesDescriptionEstimationHaute"
  | "oeuvresDansVentesDescriptionEstimationBasse"
  | "oeuvresDansVentesDescriptionVendu"
  | "oeuvresDansVentesDescriptionResultatNonListe"
  | "oeuvresDansVentesDescriptionNonVenu"
  | "oeuvresDansVentesDescriptionNonVendu"
  | "oeuvresDansVentesDescriptionNonReproduit"
  | "oeuvresDansVentesDescriptionRetire"
  | "oeuvresDansVentesDescriptionMiseAPrix"
  | "oeuvresDansVentesDescriptionPrixMarteau"
  | "oeuvresDansVentesDescriptionPrixAvecFrais"
  | "oeuvresDansVentesDescriptionNotes"
> {
  return {
    auction: tirageAuction.auction,
    oeuvresDansVentesDescriptionNumeroLot:
      tirageAuction.tirageDansVentesDescriptionNumeroLot,
    oeuvresDansVentesDescriptionReproduitPage:
      tirageAuction.tirageDansVentesDescriptionReproduitPage,
    oeuvresDansVentesDescriptionEstimationHaute:
      tirageAuction.tirageDansVentesDescriptionEstimationHaute,
    oeuvresDansVentesDescriptionEstimationBasse:
      tirageAuction.tirageDansVentesDescriptionEstimationBasse,
    oeuvresDansVentesDescriptionVendu:
      tirageAuction.tirageDansVentesDescriptionVendu,
    oeuvresDansVentesDescriptionResultatNonListe:
      tirageAuction.tirageDansVentesDescriptionResultatNonListe,
    oeuvresDansVentesDescriptionNonVenu:
      tirageAuction.tirageDansVentesDescriptionNonVenu,
    oeuvresDansVentesDescriptionNonVendu:
      tirageAuction.tirageDansVentesDescriptionNonVendu,
    oeuvresDansVentesDescriptionNonReproduit:
      tirageAuction.tirageDansVentesDescriptionNonReproduit,
    oeuvresDansVentesDescriptionRetire:
      tirageAuction.tirageDansVentesDescriptionRetire,
    oeuvresDansVentesDescriptionMiseAPrix:
      tirageAuction.tirageDansVentesDescriptionMiseAPrix,
    oeuvresDansVentesDescriptionPrixMarteau:
      tirageAuction.tirageDansVentesDescriptionPrixMarteau,
    oeuvresDansVentesDescriptionPrixAvecFrais:
      tirageAuction.tirageDansVentesDescriptionPrixAvecFrais,
    oeuvresDansVentesDescriptionNotes:
      tirageAuction.tirageDansVentesDescriptionNotes,
  };
}

/**
 * Maps an array of TirageInAuction to ArtworkInAuction format
 */
export function mapTirageInAuctionsToArtwork(
  tirageAuctions: TirageInAuction[] | undefined | null
): Pick<
  ArtworkInAuction,
  | "auction"
  | "oeuvresDansVentesDescriptionNumeroLot"
  | "oeuvresDansVentesDescriptionReproduitPage"
  | "oeuvresDansVentesDescriptionEstimationHaute"
  | "oeuvresDansVentesDescriptionEstimationBasse"
  | "oeuvresDansVentesDescriptionVendu"
  | "oeuvresDansVentesDescriptionResultatNonListe"
  | "oeuvresDansVentesDescriptionNonVenu"
  | "oeuvresDansVentesDescriptionNonVendu"
  | "oeuvresDansVentesDescriptionNonReproduit"
  | "oeuvresDansVentesDescriptionRetire"
  | "oeuvresDansVentesDescriptionMiseAPrix"
  | "oeuvresDansVentesDescriptionPrixMarteau"
  | "oeuvresDansVentesDescriptionPrixAvecFrais"
  | "oeuvresDansVentesDescriptionNotes"
>[] | undefined {
  if (!tirageAuctions || tirageAuctions.length === 0) {
    return undefined;
  }
  return tirageAuctions.map(mapTirageInAuctionToArtwork);
}

/**
 * Maps a TirageInBibliography to ArtworkInBibliography format
 */
export function mapTirageInBibliographyToArtwork(
  tirageBibliography: TirageInBibliography
): Pick<
  ArtworkInBibliography,
  | "bibliography"
  | "oeuvresDansBibliographieDescriptionReproduitPage"
  | "oeuvresDansBibliographieDescriptionCitePage"
  | "oeuvresDansBibliographieDescriptionPhotographe"
  | "oeuvresDansBibliographieDescriptionReproductionCouleur"
  | "oeuvresDansBibliographieDescriptionNonPagine"
  | "oeuvresDansBibliographieNotes"
> {
  return {
    bibliography: tirageBibliography.bibliography,
    oeuvresDansBibliographieDescriptionReproduitPage:
      tirageBibliography.tirageDansBibliographieDescriptionReproduitPage,
    oeuvresDansBibliographieDescriptionCitePage:
      tirageBibliography.tirageDansBibliographieDescriptionCitePage,
    oeuvresDansBibliographieDescriptionPhotographe:
      tirageBibliography.tirageDansBibliographieDescriptionPhotographe,
    oeuvresDansBibliographieDescriptionReproductionCouleur:
      tirageBibliography.tirageDansBibliographieDescriptionReproductionCouleur,
    oeuvresDansBibliographieDescriptionNonPagine:
      tirageBibliography.tirageDansBibliographieDescriptionNonPagine,
    oeuvresDansBibliographieNotes:
      tirageBibliography.tirageDansBibliographieNotes,
  };
}

/**
 * Maps an array of TirageInBibliography to ArtworkInBibliography format
 */
export function mapTirageInBibliographiesToArtwork(
  tirageBibliographies: TirageInBibliography[] | undefined | null
): Pick<
  ArtworkInBibliography,
  | "bibliography"
  | "oeuvresDansBibliographieDescriptionReproduitPage"
  | "oeuvresDansBibliographieDescriptionCitePage"
  | "oeuvresDansBibliographieDescriptionPhotographe"
  | "oeuvresDansBibliographieDescriptionReproductionCouleur"
  | "oeuvresDansBibliographieDescriptionNonPagine"
  | "oeuvresDansBibliographieNotes"
>[] | undefined {
  if (!tirageBibliographies || tirageBibliographies.length === 0) {
    return undefined;
  }
  return tirageBibliographies.map(mapTirageInBibliographyToArtwork);
}
